import type { Component, PropType } from "vue";

import { unique } from "radash";
import { computed } from "vue";

import type { ActionType, Color, Size } from "@/lib/components/types";

import {
  emitsDefinition,
  propsDefinition,
} from "@/lib/composables/componentComposable";

type AnchorTarget = "_blank" | "_parent" | "_self" | "_top" | null;

type UseButtonStyleProps = {
  actionType?: ActionType;
  checked?: boolean;
  color?: Color;
  disabled?: boolean;
  fluid?: boolean;
  opaque?: boolean;
  prefixIcon?: string;
  rounded?: boolean;
  size?: Size | "inline";
  suffixIcon?: string;
  textAlign?: "between" | "center" | "left" | "right";
};

type UseButtonProps = UseButtonStyleProps & {
  focused?: boolean;
  href?: string;
  invalid?: boolean;
  loading?: boolean;
  rel?: string;
  tag?: Component | "a" | "button" | "label" | "RouterLink" | "span";
  target?: AnchorTarget;
  to?: Record<string, unknown> | string;
  type?: "button" | "reset" | "submit";
};

type UseButtonEmit = (event: "click", value: PointerEvent) => void;

const styleProps = propsDefinition({
  actionType: { type: String as PropType<ActionType>, default: "primary" },
  checked: { type: Boolean, default: false },
  color: { type: String as PropType<Color>, default: "primary" },
  disabled: { type: Boolean, default: false },
  fluid: { type: Boolean, default: false },
  opaque: { type: Boolean, default: false },
  prefixIcon: { type: String, required: false },
  rounded: { type: Boolean, default: false },
  size: { type: String as PropType<Size | "inline">, default: "md" },
  suffixIcon: { type: String, required: false },
  textAlign: {
    type: String as PropType<"between" | "center" | "left" | "right">,
    default: "center",
  },
}) satisfies Record<keyof UseButtonStyleProps, unknown>;

const props = propsDefinition({
  ...styleProps,
  focused: { type: Boolean, default: false },
  href: { type: String, required: false },
  invalid: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  rel: { type: String, required: false },
  tag: {
    type: [String, Object] as PropType<
      Component | "a" | "button" | "label" | "RouterLink" | "span"
    >,
    required: false,
  },
  target: { type: String as PropType<AnchorTarget>, required: false },
  to: {
    type: [Object, String] as PropType<Record<string, unknown> | string>,
    required: false,
  },
  type: {
    type: String as PropType<"button" | "reset" | "submit">,
    default: "button",
  },
}) satisfies Record<keyof UseButtonProps, unknown>;

const emits = emitsDefinition(["click"]);

function use(props: UseButtonProps, emit: UseButtonEmit) {
  const loadingIcon = "fa-regular fa-spinner-third fa-spin";
  const prefixIcon = computed(() => {
    if (props.loading && !props.suffixIcon) {
      return loadingIcon;
    }

    return props.prefixIcon;
  });

  const suffixIcon = computed(() => {
    if (props.loading && props.suffixIcon) {
      return loadingIcon;
    }

    return props.suffixIcon;
  });

  const isDisabled = computed(() => props.loading || props.disabled);

  const tag = computed(() => {
    if (props.tag) {
      return props.tag;
    }
    if (props.href) {
      return "a";
    }

    if (props.to) {
      return "RouterLink";
    }

    return "button";
  });

  const parsedRel = computed(() => {
    if (!props.href || props.href.startsWith("/")) {
      return props.rel;
    }

    if (!props.rel) {
      return "noopener noreferrer";
    }

    return unique([...props.rel.split(" "), "noopener", "noreferrer"]).join(
      " ",
    );
  });

  const buttonProps = computed(() => {
    if (tag.value === "a") {
      return {
        href: props.href ?? "",
        target: props.target,
        rel: parsedRel.value,
      };
    }

    if (tag.value === "button") {
      return { type: props.type, disabled: isDisabled.value };
    }

    if (tag.value === "RouterLink" || props.to !== undefined) {
      return { to: props.to ?? "", disabled: isDisabled.value };
    }

    return { disabled: isDisabled.value };
  });

  const click = (event: PointerEvent) => emit("click", event);

  return {
    tag,
    buttonProps,
    prefixIcon,
    suffixIcon,
    disabled: isDisabled,
    click,
  };
}

export type { UseButtonEmit, UseButtonProps, UseButtonStyleProps };
export { emits, props, styleProps, use };
